import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { fetchData } from '../utils/api';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem('customer_user');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch {
      localStorage.removeItem('customer_user');
      return null;
    }
  });

  const [token, setToken] = useState(() => {
    return localStorage.getItem('customer_token') || null;
  });

  useEffect(() => {
    if (user) {
      localStorage.setItem(
        'customer_user',
        JSON.stringify(user)
      );
    } else {
      localStorage.removeItem('customer_user');
    }
  }, [user]);

  useEffect(() => {
    if (token) {
      localStorage.setItem('customer_token', token);
    } else {
      localStorage.removeItem('customer_token');
    }
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await fetchData('/auth/login', {
        method: 'POST',
        body: {
          email,
          password,
        },
        noCache: true,
      });

      const loginToken = response?.data?.token;
      const loginUser = response?.data?.user;

      if (!loginToken || !loginUser) {
        return {
          success: false,
          message:
            response?.message ||
            'Invalid login response.',
        };
      }

      if (loginUser.user_type !== 'customer') {
        return {
          success: false,
          message:
            'This login area is for customers only.',
        };
      }

      setToken(loginToken);
      setUser(loginUser);

      return {
        success: true,
        user: loginUser,
        token: loginToken,
      };
    } catch (err) {
      return {
        success: false,
        message:
          err?.response?.data?.message ||
          err?.message ||
          'Invalid email or password.',
      };
    }
  };

  /**
   * Update the locally stored user when an updated
   * tenant object is already available.
   */
  const updateStoredUser = useCallback(
    (updatedTenant) => {
      if (!updatedTenant) return;

      setUser((previous) => {
        if (!previous) return previous;

        return {
          ...previous,
          name:
            updatedTenant?.full_name ||
            previous.name,
          tenant: {
            ...(previous.tenant || {}),
            ...updatedTenant,
          },
        };
      });
    },
    []
  );

  /**
   * Fetch the latest authenticated customer from
   * the backend and replace the stale context user.
   */
  const refreshUser = useCallback(async () => {
    if (!token) {
      return null;
    }

    const response = await fetchData(
      '/customer/profile',
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        noCache: true,
      }
    );

    /*
     * Supports common response structures:
     *
     * { data: { user: {...} } }
     * { data: {...user} }
     * { user: {...} }
     */
    const responseData = response?.data;

    const freshUser =
      responseData?.user ||
      response?.user ||
      (
        responseData?.user_type ||
        responseData?.tenant
          ? responseData
          : null
      );

    if (freshUser) {
      setUser(freshUser);
      return freshUser;
    }

    /*
     * Some profile endpoints return only the
     * updated tenant instead of the complete user.
     */
    const freshTenant =
      responseData?.tenant ||
      (
        responseData?.full_name ||
        responseData?.business_name
          ? responseData
          : null
      );

    if (freshTenant) {
      updateStoredUser(freshTenant);
      return freshTenant;
    }

    throw new Error(
      'The profile response did not contain customer information.'
    );
  }, [token, updateStoredUser]);

  /**
   * Return a simplified representation of the
   * currently stored customer.
   *
   * This does not make a network request.
   */
  const fetchUserDetails = useCallback(async () => {
    if (!user) return null;

    const tenant = user.tenant || {};

    return {
      name: tenant.full_name || user.name,
      email: tenant.email || user.email,
      phone: tenant.telephone || '',
      address: tenant.address || '',
      country: tenant.country?.name || '',
      country_id:
        tenant.country?.id ||
        tenant.country_id ||
        '',
      state: tenant.state?.name || '',
      state_id:
        tenant.state?.id ||
        tenant.state_id ||
        '',
      channel: tenant.channel || '',
      tenant,
    };
  }, [user]);

  const logout = async () => {
    const currentToken = token;

    setUser(null);
    setToken(null);

    localStorage.removeItem('customer_user');
    localStorage.removeItem('customer_token');

    try {
      if (currentToken) {
        await fetchData('/auth/logout', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
          noCache: true,
        });
      }
    } catch {
      // Ignore logout API failure.
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        logout,
        refreshUser,
        fetchUserDetails,
        updateStoredUser,
        isAuthenticated: Boolean(user && token),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error(
      'useAuth must be used within an AuthProvider'
    );
  }

  return context;
}